/**
 * zayedtalib.com — motion layer
 * ------------------------------------------------------------------
 * Loaded as a deferred ES module from snippets/zt-head.liquid.
 *
 * Responsibilities
 *   1. Scroll reveals            [data-zt-reveal], .zt-words
 *   2. Animated counters         [data-zt-count]
 *   3. Cursor spotlight on cards .zt-card (fine pointers only)
 *   4. <zt-carousel>             testimonial slider (scroll-snap based)
 *   5. <zt-statement>            scroll-driven word highlight (JS fallback
 *                                only where CSS `animation-timeline: view()`
 *                                is unsupported)
 *   6. <zt-timeline>             About page chapter index (IntersectionObserver)
 *   7. Cart-native booking       [data-zt-book] / [data-zt-add]
 *
 * Performance notes
 *   - All observers use IntersectionObserver; there is no scroll listener
 *     except the single rAF-throttled fallback in <zt-statement>, and it
 *     performs one getBoundingClientRect per frame while the element is
 *     on screen.
 *   - Pointer work is batched through requestAnimationFrame and writes
 *     only CSS custom properties (no layout reads inside the write).
 *   - Every behaviour is skipped when `prefers-reduced-motion: reduce`.
 *   - Re-scans on Shopify theme-editor events so the customizer stays live.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

/* ------------------------------------------------------------------ */
/* 1. Reveal                                                           */
/* ------------------------------------------------------------------ */

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
);

function initReveals(root = document) {
  const targets = root.querySelectorAll('[data-zt-reveal]:not(.is-in), .zt-words:not(.is-in)');
  for (const el of targets) {
    if (REDUCED.matches) {
      el.classList.add('is-in');
      continue;
    }
    // Elements already above the fold on load reveal immediately so the
    // hero never waits for a scroll event.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      requestAnimationFrame(() => el.classList.add('is-in'));
    } else {
      revealObserver.observe(el);
    }
  }
}

/* ------------------------------------------------------------------ */
/* 2. Counters                                                         */
/* The markup already contains the final value (e.g. "500+") so the    */
/* number is crawlable; we only animate the digits.                    */
/* ------------------------------------------------------------------ */

const counterObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      runCounter(entry.target);
      counterObserver.unobserve(entry.target);
    }
  },
  { threshold: 0.4 }
);

function runCounter(el) {
  const raw = el.dataset.ztCount;
  const target = parseFloat(raw);
  if (Number.isNaN(target)) return;
  const prefix = el.dataset.prefix || '';
  const suffix = el.dataset.suffix || '';
  const decimals = (raw.split('.')[1] || '').length;
  const duration = 1600;
  const start = performance.now();
  const easeOut = (t) => 1 - Math.pow(1 - t, 4);

  function frame(now) {
    const p = Math.min(1, (now - start) / duration);
    const value = target * easeOut(p);
    el.textContent = prefix + value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
    if (p < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

function initCounters(root = document) {
  if (REDUCED.matches) return;
  for (const el of root.querySelectorAll('[data-zt-count]:not([data-zt-counted])')) {
    el.dataset.ztCounted = '1';
    counterObserver.observe(el);
  }
}

/* ------------------------------------------------------------------ */
/* 3. Spotlight                                                        */
/* One delegated listener; coordinates are flushed once per frame.    */
/* ------------------------------------------------------------------ */

let spotCard = null;
let spotX = 0;
let spotY = 0;
let spotTicking = false;

function flushSpot() {
  spotTicking = false;
  if (!spotCard) return;
  spotCard.style.setProperty('--mx', spotX + 'px');
  spotCard.style.setProperty('--my', spotY + 'px');
}

function onPointerMove(event) {
  const card = event.target.closest('.zt-card');
  if (!card) return;
  const rect = card.getBoundingClientRect(); // one read, before the rAF write
  spotCard = card;
  spotX = event.clientX - rect.left;
  spotY = event.clientY - rect.top;
  if (!spotTicking) {
    spotTicking = true;
    requestAnimationFrame(flushSpot);
  }
}

function initSpotlight() {
  if (!FINE_POINTER.matches || REDUCED.matches) return;
  document.addEventListener('pointermove', onPointerMove, { passive: true });
}

/* ------------------------------------------------------------------ */
/* 4. <zt-carousel>                                                    */
/* Native scroll-snap does the heavy lifting; JS adds arrows, dots,   */
/* autoplay and keyboard support. Works fully without JS (it's just a */
/* horizontally scrollable list).                                      */
/* ------------------------------------------------------------------ */

class ZtCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.index = 0;
    if (!this.track || this.slides.length < 2) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    // Track the active slide with an observer on the track itself rather
    // than a scroll listener.
    this.slideObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.6) this.setActive(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((s) => this.slideObserver.observe(s));

    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });

    const interval = parseInt(this.dataset.autoplay || '0', 10);
    if (interval > 0 && !REDUCED.matches) {
      this.pauseTimer = () => { clearInterval(this.timer); this.timer = null; };
      this.startTimer = () => {
        if (this.timer || document.hidden) return;
        this.timer = setInterval(() => this.go(this.index + 1), interval * 1000);
      };
      ['pointerenter', 'focusin', 'pointerdown', 'touchstart'].forEach((ev) => this.addEventListener(ev, this.pauseTimer, { passive: true }));
      ['pointerleave', 'focusout'].forEach((ev) => this.addEventListener(ev, this.startTimer));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pauseTimer() : this.startTimer()));
      // Only autoplay once the carousel is actually on screen.
      this.viewObserver = new IntersectionObserver(([e]) => (e.isIntersecting ? this.startTimer() : this.pauseTimer()), { threshold: 0.3 });
      this.viewObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.slideObserver?.disconnect();
    this.viewObserver?.disconnect();
    this.pauseTimer?.();
  }

  go(i) {
    const n = this.slides.length;
    const next = ((i % n) + n) % n;
    const slide = this.slides[next];
    const behavior = REDUCED.matches ? 'auto' : 'smooth';
    this.track.scrollTo({ left: slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2, behavior });
    this.setActive(next);
  }

  setActive(i) {
    if (i < 0 || i === this.index && this.slides[i].classList.contains('is-active')) return;
    this.index = i;
    this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
    this.dots.forEach((d, k) => {
      d.classList.toggle('is-active', k === i);
      d.setAttribute('aria-current', k === i ? 'true' : 'false');
    });
  }
}

/* ------------------------------------------------------------------ */
/* 5. <zt-statement>                                                   */
/* Words brighten as the paragraph passes through the viewport.       */
/* Modern browsers use pure-CSS scroll-driven animations (see the      */
/* block's stylesheet). Here we provide the fallback: one rAF-batched  */
/* scroll handler that writes a single custom property.                */
/* ------------------------------------------------------------------ */

class ZtStatement extends HTMLElement {
  connectedCallback() {
    if (REDUCED.matches) return;
    if (CSS.supports('animation-timeline: view()')) return; // CSS handles it
    this.classList.add('zt-statement--js');
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    this.io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      } else {
        window.removeEventListener('scroll', this.onScroll);
      }
    });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  update() {
    this.ticking = false;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    // 0 when the top of the paragraph is at 85% of the viewport,
    // 1 when its bottom reaches 35% — i.e. reading pace.
    const start = vh * 0.85;
    const end = vh * 0.35;
    const progress = (start - rect.top) / (start - end + rect.height);
    this.style.setProperty('--zt-p', Math.min(1, Math.max(0, progress)).toFixed(3));
  }
}

/* ------------------------------------------------------------------ */
/* 5b. <zt-timeline> — highlights the chapter index (About page).      */
/*     Purely progressive: the index is plain anchor links without JS. */
/* ------------------------------------------------------------------ */

class ZtTimeline extends HTMLElement {
  connectedCallback() {
    const links = [...this.querySelectorAll('[data-zt-index]')];
    const chapters = [...this.querySelectorAll('[data-zt-chapter]')];
    if (!links.length || !chapters.length) return;
    const byId = new Map(links.map((a) => [a.dataset.ztIndex, a]));
    const setCurrent = (id) => {
      for (const [key, a] of byId) a.classList.toggle('is-current', key === id);
    };
    // The chapter crossing a band 35–55% down the viewport is "current".
    this.io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setCurrent(e.target.dataset.ztChapter);
      },
      { rootMargin: '-35% 0px -45% 0px', threshold: 0 }
    );
    chapters.forEach((c) => this.io.observe(c));
    // Smooth-scroll the index links without breaking the hash (keeps :target working).
    this.addEventListener('click', (event) => {
      const a = event.target.closest('[data-zt-index]');
      if (!a || REDUCED.matches) return;
      const target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      history.replaceState(null, '', a.getAttribute('href'));
    });
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }
}

/* ------------------------------------------------------------------ */
/* 6. Cart-native actions                                              */
/* [data-zt-book]      adds the booking product (Theme settings →       */
/*                     Signature design) and opens Heritage's drawer.  */
/* [data-zt-add]       adds any variant id (used by the offer card).   */
/* Both go through /cart/add.js with `sections` so the drawer is       */
/* re-rendered by Heritage's own morph pipeline via CartAddEvent.      */
/* If anything is missing (no product configured, network error) the   */
/* button falls back to its href, so the action can never dead-end.    */
/* ------------------------------------------------------------------ */

async function addToCart(variantId, { properties = {}, sourceId = 'zt' } = {}) {
  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const body = { items: [{ id: Number(variantId), quantity: 1, properties }], sections: sections.join(','), sections_url: window.location.pathname };
  const res = await fetch((window.Theme?.routes?.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('cart add failed');
  const data = await res.json();
  const { CartAddEvent } = await import('@theme/events');
  document.dispatchEvent(new CartAddEvent({}, sourceId, { source: 'zt', itemCount: data.items?.length || 1, variantId: String(variantId), sections: data.sections }));
  const drawer = document.querySelector('cart-drawer-component');
  drawer?.open?.();
  return data;
}

function initCartActions() {
  // Offer card: swap the displayed price/compare-at when a plan is chosen.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
    const card = input.closest('[data-zt-offer]');
    if (!card) return;
    const price = card.querySelector('[data-zt-price]');
    const compare = card.querySelector('[data-zt-compare]');
    if (price && input.dataset.price) price.textContent = input.dataset.price;
    if (compare) {
      compare.textContent = input.dataset.compare || '';
      compare.parentElement.hidden = !input.dataset.compare;
    }
  });

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-zt-book], [data-zt-add]');
    if (!btn) return;
    const form = btn.closest('form');
    const variantId = btn.dataset.variantId || form?.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]')?.value;
    if (!variantId) return; // no product configured → normal link navigation
    event.preventDefault();
    btn.classList.add('is-busy');
    try {
      const properties = btn.hasAttribute('data-zt-book') ? { _zt_booking: 'consultation' } : {};
      await addToCart(variantId, { properties, sourceId: btn.id || 'zt' });
    } catch (_) {
      const fallback = btn.dataset.fallback || btn.getAttribute('href');
      if (fallback && fallback !== '#') window.location.href = fallback;
    } finally {
      btn.classList.remove('is-busy');
    }
  });
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

function scan(root = document) {
  initReveals(root);
  initCounters(root);
}

if (!customElements.get('zt-carousel')) customElements.define('zt-carousel', ZtCarousel);
if (!customElements.get('zt-statement')) customElements.define('zt-statement', ZtStatement);
if (!customElements.get('zt-timeline')) customElements.define('zt-timeline', ZtTimeline);

scan();
initSpotlight();
initCartActions();

// Theme editor: sections re-render in place, so re-run on the new nodes.
document.addEventListener('shopify:section:load', (e) => scan(e.target));
document.addEventListener('shopify:block:select', (e) => scan(e.target));

// Heritage swaps <main> during view transitions on navigation; pick up the
// fresh DOM once the new page content has been painted.
window.addEventListener('pageshow', () => scan());
document.addEventListener('DOMContentLoaded', () => scan());
